import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShieldCheck, ListOrdered, Users, Percent, SlidersHorizontal,
  Package, LifeBuoy, ScrollText, Settings, UserCog, LogOut, Menu, X, DatabaseBackup } from "lucide-react";
import { Logo } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const ALL_NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: null },
  { to: "/admin/deposits", label: "Verifications", icon: ShieldCheck, perm: "verify_deposits" },
  { to: "/admin/orders", label: "Sell Orders", icon: ListOrdered, perm: "process_orders" },
  { to: "/admin/customers", label: "Customers", icon: Users, perm: null },
  { to: "/admin/rates", label: "Rate Overrides", icon: Percent, perm: "manage_rates" },
  { to: "/admin/adjust", label: "Wallet Adjust", icon: SlidersHorizontal, perm: "wallet_adjustments" },
  { to: "/admin/products", label: "Products", icon: Package, perm: "manage_products" },
  { to: "/admin/tickets", label: "Support", icon: LifeBuoy, perm: "manage_tickets" },
  { to: "/admin/audit", label: "Audit & Recon", icon: ScrollText, perm: "view_reports" },
  { to: "/admin/officers", label: "Officers", icon: UserCog, perm: "SUPER" },
  { to: "/admin/reset", label: "Reset Center", icon: DatabaseBackup, perm: "SUPER" },
  { to: "/admin/settings", label: "Settings", icon: Settings, perm: null },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const isSuper = user?.role === "super_admin";
  const can = (perm) => {
    if (!perm) return true;
    if (perm === "SUPER") return isSuper;
    return isSuper || (user?.permissions || []).includes(perm);
  };
  const NAV = ALL_NAV.filter((n) => can(n.perm));
  const doLogout = async () => { await logout(); nav("/login"); };

  const sidebar = (
    <nav className="flex flex-col gap-1 p-3">
      <div className="px-3 py-2 mb-1">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full">
          {isSuper ? "Super Admin" : "Officer"}
        </span>
      </div>
      {NAV.map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} onClick={() => setOpen(false)} data-testid={`admin-nav-${label.toLowerCase().replace(/ & | /g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition
            ${isActive ? "bg-blue-600 text-white shadow-sm shadow-blue-600/30" : "text-slate-600 hover:bg-slate-100"}`}>
          <Icon className="w-[18px] h-[18px]" /> {label}
        </NavLink>
      ))}
      <button onClick={doLogout} data-testid="admin-logout-button"
        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 transition mt-2">
        <LogOut className="w-[18px] h-[18px]" /> Logout
      </button>
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-16 max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="admin-mobile-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Logo />
            <span className="text-xs font-semibold text-slate-400 hidden sm:inline">Admin Console</span>
          </div>
          <span className="text-sm text-slate-600" data-testid="admin-user-name">{user?.name}</span>
        </div>
      </header>
      <div className="flex max-w-[1600px] mx-auto">
        <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-4rem)] sticky top-16 self-start">
          {sidebar}
        </aside>
        {open && (
          <div className="lg:hidden fixed inset-0 z-30 top-16">
            <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl overflow-y-auto">{sidebar}</aside>
          </div>
        )}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
